import { create } from 'zustand'

import { useCurrentConversationStore } from './useCurrentConversationStore'

/**
 * useAllConversationsStore – a store for functions and state
 * related to all conversations, not the current conversation.
 *
 * @returns {Object}  The store
 */
export const useAllConversationsStore = create((set, get) => ({
  conversations: [], // [{ id, title, lastMessage, ... }]
  pagination: { limit: 10, offset: 0, total: null },
  // NOTE: `isLoading` is used by the sidebar infinite scroll hook
  isLoading: false,
  // Guard rails for infinite-scroll loops when backend returns no new items.
  lastLoadMoreAfter: null,
  loadMoreExhausted: false,
  editingTitleId: null,
  setEditingTitleId: id => {
    return set({ editingTitleId: id })
  },

  setConversations: conversationsArray =>
    set({
      conversations: conversationsArray,
    }),
  setPagination: paginationObj =>
    set({
      pagination: paginationObj,
    }),
  getConversations: () => get().conversations,
  loadConversations: async (api, limit = 15, onError = () => {}) => {
    try {
      const { body } = await api.getAllConversations({ limit })
      const { data, pagination } = body
      if (data && pagination) {
        set({
          conversations: data,
          pagination,
          lastLoadMoreAfter: null,
          loadMoreExhausted: false,
        })
      } else {
        set({
          conversations: null,
          pagination: null,
          lastLoadMoreAfter: null,
          loadMoreExhausted: false,
        })
      }
      return { data, pagination }
    } catch (err) {
      console.error('Error loading conversations', err)
      if (onError) onError(err)
    }
  },
  loadMoreConversations: async (api, onError = () => {}) => {
    const { pagination, conversations, isLoading, lastLoadMoreAfter, loadMoreExhausted } = get()
    const { total } = pagination
    if (loadMoreExhausted) return
    if (isLoading || conversations.length >= total) return
    try {
      set({ isLoading: true })
      const last = conversations[conversations.length - 1]
      const after = last?.updated_at
      if (!after) return
      if (lastLoadMoreAfter === after) return
      set({ lastLoadMoreAfter: after })
      const { body } = await api.getAllConversations({
        limit: 15,
        after,
      })
      const { data, pagination } = body
      if (data && pagination) {
        const merged = new Map()
        ;[...conversations, ...data].forEach(c => merged.set(c.id, c))
        const mergedArr = Array.from(merged.values())
        // If we didn't add any new unique conversations, stop infinite retry loops.
        if (mergedArr.length === conversations.length) {
          set({
            conversations: mergedArr,
            pagination: { ...pagination, total: mergedArr.length },
            loadMoreExhausted: true,
          })
        } else {
          set({
            conversations: mergedArr,
            pagination,
            loadMoreExhausted: false,
          })
        }
      } else {
        set({ conversations: null, pagination: null })
      }
    } catch (err) {
      console.error('Error loading more conversations', err)
      if (onError) onError(err)
    } finally {
      set({ isLoading: false })
    }
  },
  updateConversation: async (api, conversationId, data) => {
    return api.updateConversation(conversationId, data).catch(err => {
      throw err
    })
  },
  deleteConversation: async (api, conversationId) => {
    await api.deleteConversation(conversationId)

    const { id: currentId, clearCurrentConversation } =
      useCurrentConversationStore.getState()
    if (currentId === conversationId) {
      clearCurrentConversation()
    }
    get().loadConversations(api)
  },
}))

export const useConversations = () =>
  useAllConversationsStore(s => s.conversations)
